import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFieldLocked } from './FieldLock';
import { TimeField, formatTime12h } from './TimeField';

/**
 * The site-wide date control: a typed MM/DD/YYYY field plus a calendar whose month is a
 * <select> and whose year is a typeable combobox, rather than something you page to one
 * month at a time.
 *
 * It began life on eForms (`pages/eform/question-types/DateField.tsx`) as the replacement
 * for the native <input type="date">, and the reasons it replaced that control there hold
 * everywhere else on the site. The native control's only route to 1954 is a chevron held
 * down for 850 months on desktop or a spinning wheel on mobile; it ignores `placeholder`;
 * it paints at a UA-chosen width that overran its grid cell on iOS (SCRUM-1379); its
 * "reset" on Android writes today's date instead of clearing (SCRUM-1466); and it renders
 * grey beside white text fields (SCRUM-1441, SCRUM-1467). Every one of those tickets was
 * a workaround layered on a control this component simply does not use.
 *
 * The month dropdown is a real <select> on purpose: mobile browsers render it as a native
 * picker, which is the fastest control on a phone and costs us no scroll-trapping code of
 * our own. The year is different — see YearPicker below — because 120 options is too many
 * to scroll or spin through, so it takes typed digits instead.
 *
 * Dates here are plain calendar dates. Everything is built from Y/M/D integers and ISO
 * strings, and no value passes through new Date(string), which would shift a birth date
 * across a day boundary for anyone west of UTC. The value in and out is always the ISO
 * 'YYYY-MM-DD' the old native control produced, so no call site's save path changed.
 *
 * The calendar panel renders through a portal at a fixed position. Most of the fields
 * this replaced sit inside a Modal whose body scrolls (`overflow-y-auto`), and an
 * absolutely positioned panel is clipped there; a portal is not.
 */

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Rough rendered height of the panel, used only to decide whether to drop up. */
const PANEL_HEIGHT = 370;
/** The extra row the time controls add when `onTimeChange` is given. */
const TIME_ROW_HEIGHT = 60;
/** Fixed panel width. The field it belongs to may be far narrower (a `w-36` filter). */
const PANEL_WIDTH = 304;
/**
 * The compact (`xs`) panel: sized to sit inside a filter sidebar (`w-64`, 256px, minus
 * its padding) rather than spill out over the list beside it.
 */
const COMPACT_PANEL_WIDTH = 228;
const COMPACT_PANEL_HEIGHT = 300;
const PANEL_GAP = 6;
const VIEWPORT_MARGIN = 8;

export interface CalendarDate {
  year: number;
  /** 0-11, matching Date#getMonth. */
  month: number;
  day: number;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function toIsoDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseIsoDate(value: string): CalendarDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || '').trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (month < 0 || month > 11) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

/** ISO date for today, in the viewer's own calendar. */
export function todayIsoDate(): string {
  const now = new Date();
  return toIsoDate(now.getFullYear(), now.getMonth(), now.getDate());
}

/** The current clock as 'HH:MM', in the viewer's own zone. */
export function nowHhmm(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

/** 'YYYY-MM-DD' to 'MM/DD/YYYY'. Returns '' for anything unparseable. */
export function formatDisplayDate(iso: string): string {
  const parsed = parseIsoDate(iso);
  if (!parsed) return '';
  return `${String(parsed.month + 1).padStart(2, '0')}/${String(parsed.day).padStart(2, '0')}/${parsed.year}`;
}

/** Keeps typed input in MM/DD/YYYY shape. Slashes are re-derived, so backspace works. */
export function maskDateInput(raw: string): string {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** 'MM/DD/YYYY' to ISO, or null while the entry is still partial or impossible. */
export function isoFromTypedDate(text: string): string | null {
  const digits = (text || '').replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const month = Number(digits.slice(0, 2)) - 1;
  const day = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4));
  if (month < 0 || month > 11) return null;
  if (year < 1) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return toIsoDate(year, month, day);
}

/**
 * Why a typed entry cannot become the value, or null when it can. The field drops a
 * rejected entry and shows the previous date again, so the message names what was
 * typed: it has to make sense after the text it describes is gone.
 *
 * `revertedTo` is the ISO value the field went back to. Pass it once the entry has
 * actually been dropped, so the message says what the date is now ('' reads as
 * cleared); leave it out while the rejected text is still on screen.
 */
export function typedDateProblem(
  text: string,
  min: string,
  max: string,
  revertedTo?: string,
): string | null {
  const iso = isoFromTypedDate(text);
  let reason: string;
  let hint = '';
  if (!iso) {
    if ((text || '').replace(/\D/g, '').length === 8) {
      reason = `${text} is not a valid date`;
    } else {
      reason = `${text} is not a complete date`;
      hint = 'Use MM/DD/YYYY.';
    }
  } else if (iso > max) {
    reason = `${text} is too late`;
    hint = `Choose a date on or before ${formatDisplayDate(max)}.`;
  } else if (iso < min) {
    reason = `${text} is too early`;
    hint = `Choose a date on or after ${formatDisplayDate(min)}.`;
  } else {
    return null;
  }
  const previous = revertedTo === undefined ? undefined : formatDisplayDate(revertedTo);
  const outcome =
    previous === undefined
      ? ''
      : previous
        ? `, so the date has been reverted to ${previous}`
        : ', so the date has been cleared';
  return [`${reason}${outcome}.`, hint].filter(Boolean).join(' ');
}

/**
 * The selectable range when a call site gives none. A century back and a decade forward
 * covers past history and scheduling alike; birth dates get their own range below.
 */
export function defaultDateRange(): { min: string; max: string } {
  const year = new Date().getFullYear();
  return { min: `${year - 100}-01-01`, max: `${year + 10}-12-31` };
}

/** Birth dates: 120 years back, nothing in the future. Pair with `yearOrder="desc"`. */
export function birthDateRange(): { min: string; max: string } {
  const year = new Date().getFullYear();
  return { min: `${year - 120}-01-01`, max: todayIsoDate() };
}

/** ISO dates sort lexicographically, so every range check is a string compare. */
function clampIso(iso: string, min: string, max: string): string {
  if (iso < min) return min;
  if (iso > max) return max;
  return iso;
}

function viewFor(value: string, min: string, max: string): { year: number; month: number } {
  const selected = parseIsoDate(value);
  if (selected && value >= min && value <= max) {
    return { year: selected.year, month: selected.month };
  }
  const fallback = parseIsoDate(clampIso(todayIsoDate(), min, max));
  return fallback
    ? { year: fallback.year, month: fallback.month }
    : { year: new Date().getFullYear(), month: 0 };
}

export type CalendarDatePickerSize = 'xs' | 'sm' | 'md' | 'lg';

export interface CalendarDatePickerProps {
  /** ISO 'YYYY-MM-DD', or '' when unanswered. */
  value: string;
  onChange: (value: string) => void;
  /** Selectable range, inclusive, as ISO dates. Defaults to `defaultDateRange()`. */
  min?: string;
  max?: string;
  /** Newest-first suits birth dates; oldest-first suits everything else. */
  yearOrder?: 'asc' | 'desc';
  /**
   * `xs` is the filter-sidebar control: the same field as `sm`, but its calendar opens
   * small enough to sit inside the sidebar's own width; `sm` is a toolbar or inline-table
   * control; `md` matches `form-input-base` and is the default for every form on the
   * site; `lg` is the patient-facing eForm size.
   */
  size?: CalendarDatePickerSize;
  invalid?: boolean;
  disabled?: boolean;
  required?: boolean;
  /**
   * Adds an inline clear control while a value is set. The panel always carries its own
   * Clear, and an emptied text field clears too; this is the affordance for a field whose
   * value should visibly read as optional (SCRUM-1466).
   */
  clearable?: boolean;
  placeholder?: string;
  id?: string;
  /** Submitted as a hidden input carrying the ISO value, the way a native date input would. */
  name?: string;
  /**
   * Set only when no <label htmlFor> points at the field. An aria-label attribute
   * outranks a label element, so passing one alongside a label hides the label.
   */
  ariaLabel?: string;
  ariaDescribedBy?: string;
  /** Classes for the bordered field box (width, theme). */
  className?: string;
  /** Classes for the text input itself. */
  inputClassName?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Scheduling mode. When `onTimeChange` is given the calendar panel carries an
   * hour / minute / AM-PM row, the field shows the chosen time beside the date, and
   * the panel stays open after a day is picked so the time can be adjusted. `time` is
   * 'HH:MM' or '' — an empty time reads as the current clock, and that default is
   * reported through `onTimeChange` the moment a date is chosen, so the caller never
   * holds a date without a time. Adjusting the time while no date is set is reported
   * too, but it is the caller's business to apply nothing until a date exists.
   */
  time?: string;
  onTimeChange?: (time: string) => void;
  /** Earliest 'HH:MM' on the selected day; greys out a period wholly behind it. */
  timeMin?: string;
}

const SELECT_CLASS =
  'rounded-md border border-slate-200 bg-white py-1.5 pl-2 pr-1 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

interface YearPickerProps {
  /** Every selectable year, already in display order (newest-first for birth dates). */
  years: number[];
  value: number;
  onChange: (year: number) => void;
  /** Smaller type and box for the `xs` panel. */
  compact?: boolean;
}

/**
 * The year control of the calendar header: a typeable combobox rather than a <select>.
 *
 * A birth-date range is 120 years long, and a native <select> of 120 options is a long
 * scroll on desktop and a long wheel on a phone. Here the field takes digits: each one
 * narrows the list beneath it to the years that start with what has been typed, and a
 * full four-digit year jumps the calendar there with no further action. The list stays
 * for anyone who prefers to click, and arrow keys plus Enter pick from it.
 */
function YearPicker({ years, value, onChange, compact }: YearPickerProps) {
  const [text, setText] = useState(String(value));
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // The month arrows can cross a year boundary; reflect that without fighting typing.
  useEffect(() => {
    setText(String(value));
  }, [value]);

  const digits = text.replace(/\D/g, '');
  const filtering = digits !== '' && digits !== String(value);
  const matches = filtering ? years.filter((year) => String(year).startsWith(digits)) : years;

  useEffect(() => {
    if (!open || active == null) return;
    const option = listRef.current?.querySelector<HTMLElement>(`[data-year="${active}"]`);
    if (option && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  }, [open, active]);

  function openList() {
    setActive(value);
    setOpen(true);
  }

  function choose(year: number) {
    onChange(year);
    setText(String(year));
    setOpen(false);
    inputRef.current?.focus();
  }

  function handleChange(raw: string) {
    const next = raw.replace(/\D/g, '').slice(0, 4);
    setText(next);
    setOpen(true);
    const candidates = next === '' ? years : years.filter((year) => String(year).startsWith(next));
    setActive(candidates[0] ?? null);
    // Four digits inside the range is unambiguous: go there without waiting for Enter.
    if (next.length === 4 && years.includes(Number(next))) {
      onChange(Number(next));
      setOpen(false);
    }
  }

  function handleBlur() {
    // Anything short of a valid year was never a selection; show the year in view.
    setOpen(false);
    setText(String(value));
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      if (matches.length === 0) return;
      const index = active == null ? -1 : matches.indexOf(active);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = Math.min(matches.length - 1, Math.max(0, index + step));
      setActive(matches[nextIndex]);
      return;
    }
    if (event.key === 'Enter') {
      const pick = active != null && matches.includes(active) ? active : matches[0];
      if (pick != null) {
        event.preventDefault();
        choose(pick);
      }
      return;
    }
    if (event.key === 'Escape' && open) {
      // Close only the year list. Left to bubble, the picker's own Escape handler would
      // close the whole calendar.
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      setText(String(value));
    }
  }

  return (
    <div className="relative shrink-0">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={openList}
        onClick={() => !open && openList()}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        inputMode="numeric"
        autoComplete="off"
        maxLength={4}
        aria-label="Year"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active != null ? `${listId}-${active}` : undefined}
        className={cn(
          SELECT_CLASS,
          'w-16 px-1.5 text-center tabular-nums',
          compact && 'w-[3.25rem] py-1 px-1 text-xs',
        )}
      />
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Years"
          className="absolute left-0 top-full z-10 mt-1 max-h-48 w-full min-w-[5.5rem] overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
        >
          {matches.length === 0 && (
            <li className="px-2 py-1 text-xs text-slate-400" aria-disabled="true">
              No year matches
            </li>
          )}
          {matches.map((year) => (
            <li
              key={year}
              id={`${listId}-${year}`}
              data-year={year}
              role="option"
              aria-selected={year === value}
              // mousedown would blur the input and close the list before click lands.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(year)}
              onMouseEnter={() => setActive(year)}
              className={cn(
                'cursor-pointer px-2 py-1 text-center tabular-nums',
                year === active ? 'bg-slate-100 text-slate-900' : 'text-slate-700',
                year === value && 'font-semibold text-primary',
              )}
            >
              {year}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SIZE_STYLES: Record<
  CalendarDatePickerSize,
  { field: string; input: string; button: string; icon: string }
> = {
  // Same field as `sm`: only the calendar panel is compact.
  xs: {
    field: 'rounded-md border-slate-300 focus-within:ring-1 focus-within:ring-primary focus-within:border-primary',
    input: 'px-2 py-1.5 text-xs rounded-l-md',
    button: 'h-7 w-7 rounded-r-md',
    icon: 'h-3.5 w-3.5',
  },
  sm: {
    field: 'rounded-md border-slate-300 focus-within:ring-1 focus-within:ring-primary focus-within:border-primary',
    input: 'px-2 py-1.5 text-xs rounded-l-md',
    button: 'h-7 w-7 rounded-r-md',
    icon: 'h-3.5 w-3.5',
  },
  md: {
    field: 'rounded-md border-slate-300 focus-within:ring-1 focus-within:ring-primary focus-within:border-primary',
    input: 'px-3 py-2 text-sm rounded-l-md',
    button: 'h-9 w-9 rounded-r-md',
    icon: 'h-4 w-4',
  },
  lg: {
    field: 'rounded-lg border-slate-200 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary',
    input: 'px-4 py-3 text-sm rounded-l-lg',
    button: 'h-11 w-11 rounded-r-lg',
    icon: 'h-4 w-4',
  },
};

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else (ref as React.MutableRefObject<T | null>).current = value;
}

export default function CalendarDatePicker({
  value,
  onChange,
  min: minProp,
  max: maxProp,
  yearOrder = 'asc',
  size = 'md',
  invalid,
  disabled,
  required,
  clearable,
  placeholder = 'MM/DD/YYYY',
  id,
  name,
  ariaLabel,
  ariaDescribedBy,
  className,
  inputClassName,
  inputRef,
  time = '',
  onTimeChange,
  timeMin,
}: CalendarDatePickerProps) {
  const withTime = !!onTimeChange;
  /**
   * Inside a locked FieldLockScope the field shows its date (and time) as plain
   * content: the text is read-only, there is no calendar button or clear, the
   * panel never opens, and the box is not drawn disabled.
   */
  const locked = useFieldLocked();
  const compact = size === 'xs';
  const fallbackRange = useMemo(defaultDateRange, []);
  const min = minProp || fallbackRange.min;
  const max = maxProp || fallbackRange.max;

  const [open, setOpen] = useState(false);
  // Snapshot of the clock taken when the panel opens, so an unset time reads as "now"
  // without the row ticking while the user looks at it.
  const [openedAt, setOpenedAt] = useState(() => nowHhmm());
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const [text, setText] = useState(() => formatDisplayDate(value));
  const [view, setView] = useState(() => viewFor(value, min, max));
  // Set when a typed entry is refused. Without it the field quietly put the old date
  // back and a user who typed a date past the range never learned why it did not stick.
  const [rejection, setRejection] = useState<string | null>(null);
  const rejectionId = useId();

  const rootRef = useRef<HTMLDivElement>(null);
  // The bordered box itself. The panel anchors here rather than on the root, which
  // also holds the rejection message and so grows while one is showing.
  const fieldRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const textRef = useRef<HTMLInputElement | null>(null);

  // Reformat the visible text only when the value changes from outside this
  // component. Echoing back every value we ourselves just committed would fight the
  // cursor while the user is still typing.
  const committedRef = useRef(value);
  useEffect(() => {
    if (committedRef.current !== value) {
      committedRef.current = value;
      setText(formatDisplayDate(value));
      setRejection(null);
    }
  }, [value]);

  const positionPanel = useCallback(() => {
    const rect = fieldRef.current?.getBoundingClientRect();
    if (!rect) return;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(compact ? COMPACT_PANEL_WIDTH : PANEL_WIDTH, viewportWidth - VIEWPORT_MARGIN * 2);
    const height = (compact ? COMPACT_PANEL_HEIGHT : PANEL_HEIGHT) + (withTime ? TIME_ROW_HEIGHT : 0);
    let left = rect.left;
    if (left + width > viewportWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - width);
    }
    const dropUp = rect.bottom + PANEL_GAP + height > viewportHeight && rect.top > height + PANEL_GAP;
    setPanelStyle(
      dropUp
        ? { position: 'fixed', left, width, bottom: viewportHeight - rect.top + PANEL_GAP }
        : { position: 'fixed', left, width, top: rect.bottom + PANEL_GAP },
    );
  }, [withTime, compact]);

  useLayoutEffect(() => {
    if (open) positionPanel();
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    // Capture-phase scroll: the field's scrolling ancestor is usually a modal body, not
    // the window, and scroll events do not bubble.
    document.addEventListener('scroll', positionPanel, true);
    window.addEventListener('resize', positionPanel);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('scroll', positionPanel, true);
      window.removeEventListener('resize', positionPanel);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    if ((disabled || locked) && open) setOpen(false);
  }, [disabled, locked, open]);

  // A date is only ever applied together with a time. Emptying the hour or minutes
  // while the panel is open is treated as mid-edit, but once the panel closes with the
  // time still missing the pair is incomplete, so the date goes too rather than sit in
  // the field looking scheduled.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !open && withTime && value && !time) commit('');
    wasOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const minDate = useMemo(() => parseIsoDate(min), [min]);
  const maxDate = useMemo(() => parseIsoDate(max), [max]);

  const years = useMemo(() => {
    if (!minDate || !maxDate) return [];
    const list: number[] = [];
    for (let year = minDate.year; year <= maxDate.year; year++) list.push(year);
    return yearOrder === 'desc' ? list.reverse() : list;
  }, [minDate, maxDate, yearOrder]);

  /** A month is reachable when any of its days falls inside [min, max]. */
  function monthInRange(year: number, month: number): boolean {
    const first = toIsoDate(year, month, 1);
    const last = toIsoDate(year, month, daysInMonth(year, month));
    return last >= min && first <= max;
  }

  function goToMonth(year: number, month: number) {
    if (monthInRange(year, month)) {
      setView({ year, month });
      return;
    }
    // Changing year can strand the selected month outside the range: a birth-date
    // picker switched to the current year has no months after today. Snap to the
    // nearest month that still has selectable days rather than show an empty grid.
    for (let offset = 1; offset < 12; offset++) {
      if (month - offset >= 0 && monthInRange(year, month - offset)) {
        setView({ year, month: month - offset });
        return;
      }
      if (month + offset <= 11 && monthInRange(year, month + offset)) {
        setView({ year, month: month + offset });
        return;
      }
    }
  }

  function shiftMonth(delta: number) {
    const next = new Date(view.year, view.month + delta, 1);
    if (!monthInRange(next.getFullYear(), next.getMonth())) return;
    setView({ year: next.getFullYear(), month: next.getMonth() });
  }

  /** The time the panel shows and a date commit carries: the chosen one, else now. */
  const effectiveTime = time || openedAt;

  function commit(iso: string) {
    committedRef.current = iso;
    setText(formatDisplayDate(iso));
    setRejection(null);
    // A date is never handed over without its time. Report the default first, so a
    // caller composing the two sees the time before the date that completes the pair.
    if (withTime && iso && !time) onTimeChange?.(effectiveTime);
    if (withTime && !iso) onTimeChange?.('');
    onChange(iso);
  }

  function openPanel() {
    if (disabled || locked) return;
    setView(viewFor(value, min, max));
    setOpenedAt(nowHhmm());
    setOpen(true);
  }

  function handleTextChange(raw: string) {
    const masked = maskDateInput(raw);
    setText(masked);
    if (masked === '') {
      setRejection(null);
      committedRef.current = '';
      onChange('');
      return;
    }
    const iso = isoFromTypedDate(masked);
    if (iso && iso >= min && iso <= max) {
      setRejection(null);
      committedRef.current = iso;
      if (withTime && !time) onTimeChange?.(effectiveTime);
      onChange(iso);
      return;
    }
    // A complete entry that cannot be used is flagged at once, while what was typed is
    // still on screen. A partial one is still being typed, so it waits for blur.
    const complete = masked.replace(/\D/g, '').length === 8;
    setRejection(complete ? typedDateProblem(masked, min, max) : null);
  }

  function handleTextBlur() {
    // Nothing typed since the last commit. An out-of-range value handed in by the caller
    // is left alone: the user did not enter it, and nothing is being dropped.
    if (text === '' || text === formatDisplayDate(value)) return;
    // A half-typed or out-of-range entry never became the answer, so drop it, show
    // whatever the answer actually is, and say why and what the date is now.
    const problem = typedDateProblem(text, min, max, value);
    if (!problem) return;
    setRejection(problem);
    setText(formatDisplayDate(value));
  }

  function selectDay(day: number) {
    const iso = toIsoDate(view.year, view.month, day);
    if (iso < min || iso > max) return;
    commit(iso);
    // With a time row the panel stays open: the day is usually picked first and the
    // time adjusted after. Done, Escape or a click outside closes it.
    if (withTime) return;
    setOpen(false);
    triggerRef.current?.focus();
  }

  function closePanel() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function clear() {
    commit('');
    setOpen(false);
    textRef.current?.focus();
  }

  const today = todayIsoDate();
  const leadingBlanks = new Date(view.year, view.month, 1).getDay();
  const dayCount = daysInMonth(view.year, view.month);
  const prevMonth = new Date(view.year, view.month - 1, 1);
  const nextMonth = new Date(view.year, view.month + 1, 1);
  const prevDisabled = !monthInRange(prevMonth.getFullYear(), prevMonth.getMonth());
  const nextDisabled = !monthInRange(nextMonth.getFullYear(), nextMonth.getMonth());
  const clearShown = !!clearable && !!value && !disabled && !locked;
  const styles = SIZE_STYLES[size];
  const panelLabel = `${ariaLabel || 'Date'} calendar`;

  return (
    <div ref={rootRef} className="relative">
      <div
        ref={fieldRef}
        data-testid="date-field"
        className={cn(
          'flex w-full max-w-full items-center border bg-white transition-colors',
          styles.field,
          invalid && 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500',
          disabled && !locked && 'cursor-not-allowed bg-slate-50',
          className,
        )}
        style={{ boxSizing: 'border-box' }}
      >
        <input
          ref={(node) => {
            textRef.current = node;
            setRef(inputRef, node);
          }}
          type="text"
          id={id}
          value={text}
          onChange={locked ? undefined : (e) => handleTextChange(e.target.value)}
          onBlur={locked ? undefined : handleTextBlur}
          readOnly={locked || undefined}
          disabled={locked ? undefined : disabled}
          required={required}
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          aria-describedby={[ariaDescribedBy, rejection && rejectionId].filter(Boolean).join(' ') || undefined}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:text-slate-500',
            styles.input,
            inputClassName,
          )}
        />
        {name && <input type="hidden" name={name} value={value} />}

        {withTime && value && time && locked && (
          <span className="mr-1 inline-flex shrink-0 items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
            <Clock className="h-3 w-3" aria-hidden="true" />
            {formatTime12h(time)}
          </span>
        )}
        {withTime && value && time && !locked && (
          <button
            type="button"
            onClick={() => (open ? setOpen(false) : openPanel())}
            disabled={disabled}
            aria-label={`Scheduled time ${formatTime12h(time)}, open calendar to change`}
            className="mr-1 inline-flex shrink-0 items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:hover:bg-slate-100"
          >
            <Clock className="h-3 w-3" aria-hidden="true" />
            {formatTime12h(time)}
          </button>
        )}

        {clearShown && (
          <button
            type="button"
            // preventDefault before the state write: without it the click reaches the
            // input underneath and moves focus on the way out.
            onClick={(event) => {
              event.preventDefault();
              clear();
            }}
            aria-label="Clear date"
            title="Clear date"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary touch-target-44 tap-feedback"
          >
            <X className="h-3.5 w-3.5 touch-target-icon" />
          </button>
        )}

        {!locked && (
          <button
            ref={triggerRef}
            type="button"
            onClick={() => (open ? setOpen(false) : openPanel())}
            disabled={disabled}
            aria-label={open ? 'Close calendar' : 'Open calendar'}
            aria-expanded={open}
            aria-haspopup="dialog"
            className={cn(
              'flex shrink-0 items-center justify-center text-slate-400 transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:hover:text-slate-400',
              styles.button,
            )}
          >
            <CalendarIcon className={styles.icon} aria-hidden="true" />
          </button>
        )}
      </div>

      {rejection && (
        <p
          id={rejectionId}
          role="alert"
          className={cn('mt-1.5 text-red-600', size === 'lg' ? 'text-sm' : 'text-xs')}
        >
          {rejection}
        </p>
      )}

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={panelLabel}
            style={panelStyle}
            // Above the modal stack (`z-50`) and below app-wide chrome (`z-[9999]`).
            className={cn(
              'z-[60] rounded-xl border border-slate-200 bg-white text-slate-700 shadow-lg',
              compact ? 'p-2' : 'p-3',
            )}
          >
            <div className={cn('flex items-center', compact ? 'mb-1.5 gap-1' : 'mb-2 gap-1.5')}>
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                disabled={prevDisabled}
                aria-label="Previous month"
                className={cn(
                  'flex shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent',
                  compact ? 'h-6 w-6' : 'h-8 w-8',
                )}
              >
                <ChevronLeft className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
              </button>

              <select
                value={view.month}
                onChange={(e) => goToMonth(view.year, Number(e.target.value))}
                aria-label="Month"
                className={cn('min-w-0 flex-1', SELECT_CLASS, compact && 'py-1 pl-1.5 pr-0.5 text-xs')}
              >
                {MONTH_NAMES.map((monthName, index) => (
                  <option key={monthName} value={index} disabled={!monthInRange(view.year, index)}>
                    {monthName}
                  </option>
                ))}
              </select>

              <YearPicker
                years={years}
                value={view.year}
                onChange={(year) => goToMonth(year, view.month)}
                compact={compact}
              />

              <button
                type="button"
                onClick={() => shiftMonth(1)}
                disabled={nextDisabled}
                aria-label="Next month"
                className={cn(
                  'flex shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent',
                  compact ? 'h-6 w-6' : 'h-8 w-8',
                )}
              >
                <ChevronRight className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
              </button>
            </div>

            {/* Seven columns at every width — a week does not reflow, so this is one of
                the few grids that is deliberately not "grid-cols-1 md:grid-cols-N". The
                template is inline rather than a `grid-cols-7` utility so it reads as a
                fixed calendar week instead of an unresponsive layout waiting to be fixed. */}
            <div
              className="grid gap-0.5"
              style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
            >
              {WEEKDAY_LABELS.map((label) => (
                <div
                  key={label}
                  className={cn(
                    'pb-1 text-center font-medium text-slate-400',
                    compact ? 'text-[10px]' : 'text-[11px]',
                  )}
                >
                  {label}
                </div>
              ))}
              {Array.from({ length: leadingBlanks }, (_, index) => (
                <div key={`blank-${index}`} />
              ))}
              {Array.from({ length: dayCount }, (_, index) => index + 1).map((day) => {
                const iso = toIsoDate(view.year, view.month, day);
                const dayDisabled = iso < min || iso > max;
                const isSelected = iso === value;
                const isToday = iso === today;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => selectDay(day)}
                    disabled={dayDisabled}
                    aria-label={`${MONTH_NAMES[view.month]} ${day}, ${view.year}`}
                    aria-pressed={isSelected}
                    aria-current={isToday ? 'date' : undefined}
                    className={`${compact ? 'h-7 text-xs' : 'h-9 text-sm'} rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                      isSelected
                        ? 'bg-primary font-semibold text-white'
                        : dayDisabled
                          ? 'cursor-not-allowed text-slate-300'
                          : isToday
                            ? 'font-semibold text-primary ring-1 ring-inset ring-primary/40 hover:bg-slate-100'
                            : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {withTime && (
              <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
                <span className="text-xs font-medium text-slate-500">Time</span>
                <TimeField
                  value={effectiveTime}
                  onChange={(next) => onTimeChange?.(next)}
                  min={timeMin}
                  ariaLabel="Time"
                  className="border-slate-200 py-1"
                  wrapperClassName="w-auto"
                />
              </div>
            )}

            <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
              <button
                type="button"
                onClick={() => {
                  if (today < min || today > max) return;
                  commit(today);
                  if (withTime) return;
                  closePanel();
                }}
                disabled={today < min || today > max}
                className={cn(
                  'rounded-md px-2 py-1 font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40',
                  compact ? 'text-[11px]' : 'text-xs',
                )}
              >
                Today
              </button>
              <div className="flex items-center gap-1">
                {value && (
                  <button
                    type="button"
                    onClick={() => {
                      commit('');
                      closePanel();
                    }}
                    className={cn(
                      'rounded-md px-2 py-1 font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700',
                      compact ? 'text-[11px]' : 'text-xs',
                    )}
                  >
                    Clear
                  </button>
                )}
                {withTime && (
                  <button
                    type="button"
                    onClick={closePanel}
                    className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
